// AuthShell — shared layout for /login, /register, /forgot-password, etc.
// Mobile-first per mockup/MOBILE.md §3 — 100vh paper background, max 480px
// centered card, paper-deep channel-form, hairline border, single shadow.
//
// Apple HIG refactor (2026-09-22):
//   - 56px glass nav bar with persistent back-arrow + large title (Apple's
//     "large-title nav" pattern).  Every auth page must pass backHref except
//     the entry root (/login), so the back affordance is consistent.
//   - Card uses rounded-2xl (18px) + p-6 (24px) instead of rounded-lg + p-6.

import { type ReactNode } from "react";
import { ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { BrandLockup } from "@/components/brand/BrandLockup";

interface AuthShellProps {
  title: string;
  subtitle?: string;
  backHref?: string;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * W46.2: swap the bare mark for the full stacked lockup — mark, wordmark,
   * slogan. **Set on `/register` only.**
   *
   * A slogan is an introduction, and `/login`'s visitor has already had one.
   * Putting it on every auth page would make it chrome, which is the opposite of
   * what a line of positioning copy is for — and it would shrink and grow the
   * header as someone moves between the two screens, which reads as a bug even
   * when it is a decision.
   */
  showBrandLockup?: boolean;
}

export function AuthShell({
  title,
  subtitle,
  backHref,
  children,
  footer,
  showBrandLockup = false,
}: AuthShellProps) {
  return (
    <div data-testid="auth-shell" className="min-h-[100dvh] w-full bg-paper flex flex-col">
      <header className="safe-top px-4 h-14 flex items-center gap-2 glass-1 hairline-b">
        {backHref ? (
          <Link
            to={backHref}
            data-testid="auth-shell-back"
            className="press tap-target -ml-2 px-2 inline-flex items-center justify-center text-you-strong hover:text-you transition-colors"
            aria-label="返回"
          >
            <ChevronLeft className="w-5 h-5" aria-hidden />
          </Link>
        ) : (
          // Reserve space when no backHref so the title doesn't shift between
          // auth pages — keeps the nav bar visually stable across the flow.
          <span className="w-9 h-9 -ml-2" aria-hidden />
        )}
        <h1 className="font-display text-title-2 text-ink-1 truncate">{title}</h1>
      </header>
      <main className="flex-1 flex items-start justify-center px-4 py-8">
        <div className="w-full max-w-[420px]">
          {/* W46.1 (ADR-0129): the mark, above the form on every auth page.
              This is the app's first impression and its only surface that a
              logged-out person ever sees — the headers only render inside the
              shell, so without this the brand assets appear nowhere until after
              the person has already decided whether they trust the thing.

              W46.2: `/register` passes `showBrandLockup`, which replaces this
              block with the full stacked lockup (mark + wordmark + slogan).
              `lg` (48px) either way — the mark is the same object in both, and
              swapping the whole block rather than restyling it keeps the
              decision in one place.

              `mb-6` to match the subtitle's own bottom margin, so the mark and
              the subtitle sit on one vertical rhythm rather than two. */}
          <div className="flex justify-center mb-6">
            {showBrandLockup ? (
              <BrandLockup variant="stacked" size="lg" />
            ) : (
              <BrandLockup variant="mark-only" size="lg" />
            )}
          </div>
          {subtitle ? <p className="text-body text-ink-2 mb-6">{subtitle}</p> : null}
          <div className="surface-plain rounded-2xl border border-hairline shadow-card p-6 flex flex-col gap-5">
            {children}
          </div>
          {footer ? <div className="mt-6 text-center text-body-sm text-ink-2">{footer}</div> : null}
        </div>
      </main>
    </div>
  );
}
